// Home, permissions, the tools bubble, the markdown reference, progress, models, influence and the orb, as pictures.
import { COLOR } from '../tokens.js'
import { INK, DIM, FAINT, ACCENT, MINT, HOT, BLUE, GLASS, GLASS_EDGE, CONTROL, CONTROL_HI, SANS, MONO, box, fillBox, capsule, glass, clip, wrap, markdown, paneHeader, capsuleWidth, ago, sessionFrame } from './paint.js'

/// A permission asked in the chat (PromptCards.swift): the card under the agent's line, with the three allows,
/// each explained, and Reject.
export function permissionCard(c, w, h) {
  const body = sessionFrame(c, w, h, '▤  Transcript')
  const left = 34
  const colW = Math.round(w * 0.6)
  c.fillStyle = MINT; c.font = `700 12px ${SANS}`; c.textBaseline = 'middle'
  c.fillText('CODY', left, 146)
  c.fillStyle = 'rgba(255,255,255,0.82)'; c.font = `400 15px ${SANS}`
  c.fillText('I need to run the tests. May I?', left, 168)
  const cardY = 196
  fillBox(c, left, cardY, colW, 236, 14, 'rgba(255,255,255,0.10)')
  c.fillStyle = INK; c.font = `600 17px ${SANS}`
  c.fillText('✋  Allow bash?', left + 18, cardY + 28)
  fillBox(c, left + 18, cardY + 50, colW - 36, 34, 8, 'rgba(0,0,0,0.28)')
  c.fillStyle = 'rgba(255,255,255,0.85)'; c.font = `400 13px ${MONO}`
  c.fillText(clip(c, 'swift test --filter AirKeys', colW - 60), left + 30, cardY + 67)
  let x = left + 18
  const bw = [['Allow Once', COLOR.green, COLOR.greenInk], ['This Session', CONTROL, INK], ['Always', CONTROL, INK], ['Reject', 'rgba(255,90,80,0.35)', INK]]
  for (const [label, fill, ink] of bw) {
    const cw = capsuleWidth(c, label, 14) + 30
    capsule(c, x, cardY + 100, cw, 36, label, { fill, colour: ink, size: 14, weight: 600 })
    x += cw + 8
  }
  c.fillStyle = FAINT; c.font = `400 12px ${SANS}`
  wrap(c, 'Allow Once: just this time. This Session: this time and every time this session asks the same thing again; forgotten with the session, kept on the headset. Always: saved by this Mac\'s opencode for the project, allowed in every session there, until removed under Permissions.', left + 18, cardY + 160, colW - 36, 17)
  // the same card can be answered from the orb: the orb says "answer below" in orange
  const ox = left + colW + 40
  c.fillStyle = FAINT; c.font = `600 13px ${SANS}`; c.fillText('ALSO ON THE ORB', ox, 150)
  const g = c.createRadialGradient(ox + 30, 200, 6, ox + 40, 210, 40)
  g.addColorStop(0, '#fff'); g.addColorStop(0.4, COLOR.orange); g.addColorStop(1, 'rgba(0,0,0,0.35)')
  c.fillStyle = g; c.beginPath(); c.arc(ox + 40, 210, 40, 0, Math.PI * 2); c.fill()
  c.fillStyle = COLOR.orange; c.font = `500 12px ${SANS}`; c.fillText('answer below', ox + 90, 200)
  c.fillStyle = DIM; c.fillText('tap the orb to answer', ox + 90, 220)
  capsule(c, ox, 270, 110, 32, 'Allow Once', { fill: COLOR.green, colour: COLOR.greenInk, size: 13, weight: 600 })
  capsule(c, ox + 118, 270, 78, 32, 'Reject', { fill: 'rgba(255,90,80,0.35)', size: 13, weight: 600 })
}

/// The Permissions panel (PermissionsView.swift): a small glass panel, per Mac the rules opencode saved for the
/// project ("Always") and the allows the headset keeps for this session, each with Remove.
export function permissions(c, w, h) {
  glass(c, w, h, { radius: 26 })
  c.textBaseline = 'middle'
  c.fillStyle = INK; c.font = `600 17px ${SANS}`
  c.fillText('✋  Permissions', 24, 30)
  capsule(c, w - 60, 14, 36, 32, '↻', { size: 14 })
  c.fillStyle = DIM; c.font = `400 12px ${SANS}`; c.fillText('Demo Mac', 24, 62)
  const rows = [['Always', COLOR.tint, 'bash', 'npm test'], ['Always', COLOR.tint, 'edit', 'src/**'], ['This session', COLOR.orange, 'bash', 'git push']]
  let y = 90
  for (const [badge, tint, title, detail] of rows) {
    c.fillStyle = tint; c.font = `500 11px ${SANS}`; c.fillText(badge, 24, y)
    c.fillStyle = INK; c.font = `600 14px ${SANS}`; c.fillText(title, 112, y)
    c.fillStyle = DIM; c.font = `400 12px ${MONO}`; c.fillText(clip(c, detail, w - 240), 156, y)
    capsule(c, w - 58, y - 14, 34, 28, '✕', { size: 12 })
    fillBox(c, 24, y + 16, w - 48, 1, 0, 'rgba(255,255,255,0.10)')
    y += 36
  }
  c.fillStyle = FAINT; c.font = `400 12px ${SANS}`
  wrap(c, 'Always: saved on the Mac for the project. This session: kept on the headset until the session ends.', 24, y + 8, w - 48, 17)
}

/// The tools bubble (ToolsBubble.swift): one small glass capsule with the input methods, under the window you
/// summoned it from.
export function toolsBubble(c, w, h) {
  c.clearRect(0, 0, w, h)
  fillBox(c, 1, 1, w - 2, h - 2, h / 2, GLASS)
  c.strokeStyle = GLASS_EDGE; c.lineWidth = 2; box(c, 1, 1, w - 2, h - 2, h / 2); c.stroke()
  c.textBaseline = 'middle'
  let x = 22
  const items = [['⇩ Paste from Mac', false], ['⇧ Send to Mac', false], ['|', false], ['⌨ Air Keyboard', true], ['◎ Recenter', false], ['✋ Hand Skeleton', false], ['|', false]]
  for (const [label, on] of items) {
    if (label === '|') { fillBox(c, x + 4, h / 2 - 11, 1, 22, 0, 'rgba(255,255,255,0.2)'); x += 18; continue }
    const cw = capsuleWidth(c, label, 14) + 26
    capsule(c, x, h / 2 - 17, cw, 34, label, { fill: on ? CONTROL_HI : 'transparent', size: 14 })
    x += cw + 8
  }
  c.fillStyle = DIM; c.font = `400 12px ${SANS}`
  c.fillText(clip(c, 'hello wor → the text field you tapped', w - x - 20), x + 4, h / 2 - 8)
  c.fillText(clip(c, 'L 5/5 · R 4/5 tips sure', w - x - 20), x + 4, h / 2 + 10)
}

/// Home (HomeView.swift): the toolbar of everything, each paired Mac with its status and sessions, and the
/// long-press checklist that deletes several sessions at once.
/// Where the live session rows are in the Home window, for a tap to land on one: the first row's band starts at
/// `top` logical pixels and each is `height` tall (the same numbers the painter uses below).
export const HOME_ROWS = { top: 178, height: 48, bin: 70 }   // bin: how far from the right edge the delete zone starts

export function home(c, w, h, live = null) {
  const body = glass(c, w, h, { bar: true })
  c.textBaseline = 'middle'
  c.fillStyle = INK; c.font = `700 24px ${SANS}`
  c.fillText('Cody McCodeface', 30, 44)
  let x = w - 30
  for (const glyph of ['⋯', '＋', '⌘', '⌗', '✦']) { x -= 44; capsule(c, x, 24, 40, 40, glyph, { size: 16 }) }
  // a Mac, online
  let y = 100
  fillBox(c, 22, y - 22, w - 44, 60, 14, 'rgba(255,255,255,0.10)')
  c.fillStyle = INK; c.font = `600 16px ${SANS}`; c.fillText(clip(c, '🖥 ' + (live ? live.mac : 'Demo Mac'), w - 140), 40, y - 4)
  c.fillStyle = DIM; c.font = `400 13px ${SANS}`; c.fillText(live ? `opencode · online · ${live.sessions.length} session${live.sessions.length === 1 ? '' : 's'}` : 'opencode · online · 3 sessions', 40, y + 16)
  capsule(c, w - 84, y - 14, 44, 32, '⋯', { size: 15 })
  y += 62
  if (live) {
    // the real list: every session on that Mac, newest first, the open one marked
    c.fillStyle = FAINT; c.font = `600 12px ${SANS}`; c.fillText('SESSIONS', 34, y); y += 28
    const fit = Math.max(1, Math.floor((body - 40 - y) / 48))
    for (const s of live.sessions.slice(0, fit)) {
      const open = (live.openIDs || []).includes(s.id)
      if (open) fillBox(c, 22, y - 12, w - 44, 42, 10, 'rgba(255,255,255,0.10)')
      c.fillStyle = open ? HOT : 'rgba(255,255,255,0.5)'; c.beginPath(); c.arc(44, y + 8, 5, 0, Math.PI * 2); c.fill()
      c.fillStyle = INK; c.font = `500 15px ${SANS}`; c.fillText(clip(c, s.title, w - 130), 66, y)
      c.fillStyle = DIM; c.font = `400 12px ${SANS}`; c.fillText(clip(c, (s.directory.replace(/^\/Users\/[^/]+/, '~') || 'no folder') + ' · ' + ago(s.updatedAt), w - 150), 66, y + 19)
      // the bin: tapped once it asks, tapped again it deletes
      if (live.pendingDelete === s.id) capsule(c, w - 128, y - 6, 100, 30, 'Delete?', { fill: 'rgba(255,80,80,0.85)', colour: '#fff', size: 13, weight: 700 })
      else { c.fillStyle = 'rgba(255,255,255,0.55)'; c.font = `400 17px ${SANS}`; c.textAlign = 'center'; c.fillText('🗑', w - 44, y + 9); c.textAlign = 'left' }
      y += 48
    }
    if (!live.sessions.length) { c.fillStyle = FAINT; c.font = `400 14px ${SANS}`; c.fillText('No sessions on this Mac yet — ＋ starts one.', 34, y + 8) }
    else if (live.sessions.length > fit) capsule(c, 30, y - 6, 190, 30, `⌄ ${live.sessions.length - fit} more below`, { size: 13 })
    return
  }
  // Waiting on you
  c.fillStyle = FAINT; c.font = `600 12px ${SANS}`; c.fillText('WAITING ON YOU', 34, y); y += 24
  fillBox(c, 22, y - 18, w - 44, 40, 10, 'rgba(255,170,80,0.18)')
  c.fillStyle = INK; c.font = `500 14px ${SANS}`; c.fillText('Retune the keyboard model — allow the edit?', 40, y + 2)
  y += 46
  // the list, in selection mode: long-press made it a checklist
  c.fillStyle = FAINT; c.font = `600 12px ${SANS}`; c.fillText('SESSIONS', 34, y)
  capsule(c, w - 262, y - 14, 84, 28, 'Select All', { size: 12 })
  capsule(c, w - 172, y - 14, 62, 28, 'Cancel', { size: 12 })
  capsule(c, w - 104, y - 14, 82, 28, '🗑 Delete 2', { fill: 'rgba(255,90,80,0.35)', size: 12, weight: 600 })
  y += 28
  const sessions = [['Fix the quit hang', '~/code/xr-harness · 4 minutes ago', true], ['Add the sandbox test', '~/code/xr-harness · yesterday', true], ['Design tokens', '~/code/app · 3 days ago', false]]
  for (const [title, line, checked] of sessions) {
    if (checked) { c.fillStyle = BLUE; c.beginPath(); c.arc(44, y + 8, 10, 0, Math.PI * 2); c.fill(); c.strokeStyle = '#fff'; c.lineWidth = 2.2; c.beginPath(); c.moveTo(39, y + 8); c.lineTo(42.5, y + 11.5); c.lineTo(49, y + 4); c.stroke() }
    else { c.strokeStyle = 'rgba(255,255,255,0.5)'; c.lineWidth = 1.6; c.beginPath(); c.arc(44, y + 8, 10, 0, Math.PI * 2); c.stroke() }
    c.fillStyle = INK; c.font = `500 15px ${SANS}`; c.fillText(clip(c, title, w - 130), 66, y)
    c.fillStyle = DIM; c.font = `400 12px ${SANS}`; c.fillText(clip(c, line, w - 130), 66, y + 19)
    y += 48
  }
  capsule(c, 30, y - 6, 170, 30, '⌄ Show all 12 sessions', { size: 13 })
  if (y + 40 < body) { c.fillStyle = FAINT; c.font = `400 12px ${SANS}`; c.fillText('Long-press a row: only that Mac\'s list becomes a checklist.', 30, body - 24) }
}

/// What's New › Markdown (MarkdownReference.swift, docs/markdown-reference.md): every construct the transcript
/// renders, the source beside its native rendering, with the sections down the left.
export function markdownReference(c, w, h) {
  const body = glass(c, w, h, { bar: true })
  c.textBaseline = 'middle'
  c.fillStyle = INK; c.font = `700 22px ${SANS}`
  c.fillText('Markdown', 30, 44)
  capsule(c, w - 96, 26, 66, 36, 'Done', { size: 14, weight: 600 })
  const sections = ['Headings', 'Paragraphs and inline styles', 'Bullet lists', 'Numbered lists', 'Task lists', 'Quotes', 'Nested quotes', 'Tables', 'Rules', 'Fenced code', 'Long line', 'Mixed', 'Rich interface']
  let y = 96
  sections.forEach((s, i) => {
    if ([4, 6, 7].includes(i)) fillBox(c, 22, y - 15, 196, 30, 8, 'rgba(255,255,255,0.12)')
    c.fillStyle = [4, 6, 7].includes(i) ? INK : DIM; c.font = `${[4, 6, 7].includes(i) ? 600 : 400} 14px ${SANS}`; c.fillText(s, 34, y)
    y += 32
  })
  fillBox(c, 232, 84, 1, body - 110, 0, 'rgba(255,255,255,0.14)')
  // three sections, source left and rendered right
  const sx = 250, half = (w - sx - 40) / 2
  const show = (title, source, draw, top) => {
    c.fillStyle = INK; c.font = `600 16px ${SANS}`; c.fillText(title, sx, top)
    fillBox(c, sx, top + 18, half - 12, source.length * 19 + 16, 10, 'rgba(0,0,0,0.28)')
    c.fillStyle = 'rgba(255,255,255,0.8)'; c.font = `400 12px ${MONO}`
    source.forEach((line, i) => c.fillText(line, sx + 12, top + 36 + i * 19))
    draw(sx + half + 8, top + 30)
    return top + Math.max(source.length * 19 + 50, 120)
  }
  let top = 96
  top = show('Task lists', ['- [x] Done', '- [ ] Not yet', '- [X] Also done'], (x, y0) => markdown(c, [['task', [[true, 'Done'], [false, 'Not yet'], [true, 'Also done']]]], x, y0, half - 20), top)
  top = show('Nested quotes', ['> Outer', '> > Inner', '> > > Innermost', '>', '> Back to outer'], (x, y0) => markdown(c, [['quote', [[0, 'Outer'], [1, 'Inner'], [2, 'Innermost'], [0, 'Back to outer']]]], x, y0, half - 20), top)
  top = show('Tables', ['| Column | Left | Right |', '|---|:---|---:|', '| a | left | 1 |', '| b | **bold** | 22 |', '| c | `code` | 333 |'], (x, y0) => markdown(c, [['table', [['Column', 'Left', 'Right'], ['a', 'left', '1'], ['b', 'bold', '22'], ['c', 'code', '333']]]], x, y0, half - 20), top)
  c.fillStyle = FAINT; c.font = `400 12px ${SANS}`
  c.fillText('Tap a header to sort; long-press the table for Open in Its Own Window and Copy as CSV.', sx, Math.min(top + 10, body - 24))
}

export function progress(c, w, h, live = null) {
  const body = glass(c, w, h, { bar: true })
  c.textBaseline = 'middle'
  c.fillStyle = INK
  c.font = `700 25px ${SANS}`
  c.fillText('Progress', 34, 46)
  fillBox(c, w - 78, 26, 44, 44, 22, CONTROL)
  c.fillStyle = INK; c.font = `500 18px ${SANS}`; c.textAlign = 'center'; c.fillText('⌂', w - 56, 48); c.textAlign = 'left'

  let agents = [
    ['1', 'Fix the quit hang', '3 of 5 done · Move the Docker cleanup off the main actor', 'Demo Mac · cody-demo', MINT],
    ['2', 'Add the sandbox test', '1 of 4 done · Firewall RFC1918 from inside', 'Demo Mac · cody-demo', ACCENT],
    ['3', 'Retune the hand keyboard model', 'waiting on you: allow the edit?', 'Demo Mac · xr-harness', ACCENT],
  ]
  if (live) {
    agents = live.sessions.slice(0, 8).map((s, i) => {
      const open = s.title === live.title
      const done = live.todos.filter((t) => t.done).length, next = live.todos.find((t) => !t.done)
      const line = open ? (live.todos.length ? `${done} of ${live.todos.length} done` + (next ? ' · ' + next.text : '') : live.status === 'busy' ? 'working' : 'idle') : ago(s.updatedAt)
      return [String(i + 1), s.title, line, live.mac + ' · ' + (s.directory.split('/').pop() || '~'), open && live.status === 'busy' ? MINT : DIM]
    })
    if (!agents.length) { c.fillStyle = FAINT; c.font = `400 14px ${SANS}`; c.fillText('No sessions on this Mac yet.', 34, 110); return }
  }
  let y = 110
  for (const [n, title, line, where, colour] of agents) {
    if (y > body - 60) break
    fillBox(c, 30, y - 22, w - 60, 78, 14, 'rgba(255,255,255,0.12)')
    c.fillStyle = INK; c.font = `600 17px ${SANS}`; c.fillText(n, 46, y)
    c.fillStyle = colour; c.beginPath(); c.arc(76, y, 5, 0, Math.PI * 2); c.fill()
    c.fillStyle = INK; c.font = `600 16px ${SANS}`; c.fillText(title, 90, y)
    c.fillStyle = DIM; c.font = `400 14px ${SANS}`; c.fillText(clip(c, line, w - 150), 90, y + 22)
    c.fillStyle = FAINT; c.font = `400 12px ${SANS}`; c.fillText(where, 90, y + 42)
    y += 90
  }
}

export function models(c, w, h, live = null) {
  const body = glass(c, w, h, { radius: 38 })
  if (live) return liveModels(c, w, h, body, live)
  c.textBaseline = 'middle'
  capsule(c, 30, 24, 76, 36, 'Done', { size: 15, weight: 600 })
  // the filter, as a segmented capsule
  const seg = ['All', 'Favorites', 'Free']
  let x = w / 2 - 130
  seg.forEach((label, i) => {
    capsule(c, x, 24, 84, 36, label, { fill: i === 0 ? CONTROL_HI : 'rgba(255,255,255,0.10)', size: 14, weight: i === 0 ? 600 : 500 })
    x += 88
  })
  fillBox(c, 30, 76, w - 60, 40, 20, 'rgba(255,255,255,0.14)')
  c.fillStyle = FAINT; c.font = `400 15px ${SANS}`
  c.fillText('⌕  Search 7 models — name, provider, "free"', 48, 96)

  c.fillStyle = FAINT; c.font = `600 12px ${SANS}`; c.fillText('OPENCODE ZEN', 34, 146)
  const rows = [['Big Pickle', 'Default', true], ['Grok Code Fast', 'free', false], ['GLM 4.6', 'free', false], ['Kimi K2', 'free', false]]
  let y = 178
  for (const [name, tag, chosen] of rows) {
    if (y > body - 30) break
    fillBox(c, 30, y - 18, w - 60, 44, 12, chosen ? 'rgba(255,255,255,0.20)' : 'rgba(255,255,255,0.08)')
    if (chosen) { c.fillStyle = HOT; c.beginPath(); c.arc(50, y + 4, 6, 0, Math.PI * 2); c.fill() }
    c.fillStyle = INK; c.font = `500 16px ${SANS}`; c.fillText(name, 68, y + 4)
    c.fillStyle = DIM; c.font = `400 13px ${SANS}`; c.fillText(tag, 68 + c.measureText(name).width + 24, y + 4)
    c.textAlign = 'right'; c.fillStyle = 'rgba(255,255,255,0.55)'; c.font = `400 17px ${SANS}`
    c.fillText(chosen ? '★' : '☆', w - 48, y + 4)
    c.textAlign = 'left'
    y += 52
  }
}

/// The models the Mac's opencode offers, grouped by provider, the default marked.
function liveModels(c, w, h, body, live) {
  c.textBaseline = 'middle'
  capsule(c, 30, 24, 76, 36, 'Done', { size: 15, weight: 600 })
  const seg = ['All', 'Favorites', 'Free']
  let x = w / 2 - 130
  seg.forEach((label, i) => { capsule(c, x, 24, 84, 36, label, { fill: i === 0 ? CONTROL_HI : 'rgba(255,255,255,0.10)', size: 14, weight: i === 0 ? 600 : 500 }); x += 88 })
  const count = live.providers.reduce((n, p) => n + p.models.length, 0)
  fillBox(c, 30, 76, w - 60, 40, 20, 'rgba(255,255,255,0.14)')
  c.fillStyle = FAINT; c.font = `400 15px ${SANS}`
  c.fillText(count ? `⌕  Search ${count} model${count === 1 ? '' : 's'} — name, provider, "free"` : '⌕  Asking the Mac for its models…', 48, 96)
  let y = 146
  for (const p of live.providers) {
    if (y > body - 40) break
    c.fillStyle = FAINT; c.font = `600 12px ${SANS}`; c.fillText(p.name.toUpperCase(), 34, y); y += 32
    for (const m of p.models) {
      if (y > body - 30) break
      const chosen = live.defaultModel === p.id + '/' + m.id
      fillBox(c, 30, y - 18, w - 60, 44, 12, chosen ? 'rgba(255,255,255,0.20)' : 'rgba(255,255,255,0.08)')
      if (chosen) { c.fillStyle = HOT; c.beginPath(); c.arc(50, y + 4, 6, 0, Math.PI * 2); c.fill() }
      c.fillStyle = INK; c.font = `500 16px ${SANS}`; c.fillText(clip(c, m.name, w - 200), 68, y + 4)
      const tag = chosen ? 'Default' : m.free ? 'free' : ''
      if (tag) { c.fillStyle = DIM; c.font = `400 13px ${SANS}`; c.fillText(tag, 68 + c.measureText(m.name).width + 24, y + 4) }
      c.textAlign = 'right'; c.fillStyle = 'rgba(255,255,255,0.55)'; c.font = `400 17px ${SANS}`
      c.fillText(chosen ? '★' : '☆', w - 48, y + 4)
      c.textAlign = 'left'
      y += 52
    }
    y += 8
  }
}

const BOARD = [
  ['feature', 'Passkeys for the tunnel', 1240, 9],
  ['feature', 'Steam Frame build', 860, 6],
  ['bug', 'Pointer jumps to gaze on a tap', 410, 4],
  ['feature', 'Own domain for the tunnel', 305, 3],
  ['feature', 'Stream at 60 fps', 120, 2],
]

export function influence(c, w, h) {
  glass(c, w, h, { bar: true, radius: 30 })
  paneHeader(c, 26, 48, '✦  Influence')
  c.font = `500 15px ${SANS}`; c.fillStyle = DIM
  c.fillText('Stake points on what gets built next. Staked points come back when it ships or is declined.', 28, 80)
  let y = 112
  const most = BOARD[0][2]
  for (const [kind, title, staked, backers] of BOARD) {
    fillBox(c, 22, y, w - 44, 84, 14, COLOR.control)
    capsule(c, 36, y + 14, 66, 24, kind, { fill: kind === "bug" ? COLOR.orange : COLOR.tint, colour: "#fff", size: 12, weight: 600 })
    capsule(c, 36, y + 44, 74, 22, staked > 800 ? "✓ shipping" : "open", { fill: staked > 800 ? COLOR.green : CONTROL, colour: staked > 800 ? COLOR.greenInk : DIM, size: 11, weight: 600 })
    c.font = `600 17px ${SANS}`; c.fillStyle = INK; c.textAlign = 'left'; c.textBaseline = 'alphabetic'
    c.fillText(title, 112, y + 33)
    c.font = `500 14px ${SANS}`; c.fillStyle = DIM
    c.fillText(`${staked.toLocaleString('en-US')} points · ${backers} backers`, 112, y + 56)
    fillBox(c, 112, y + 66, (w - 44 - 112) * (staked / most), 6, 3, COLOR.tint)
    capsule(c, w - 122, y + 24, 88, 34, 'Stake', { size: 14, weight: 600 })
    y += 96
  }
  c.font = `500 14px ${SANS}`; c.fillStyle = DIM
  c.fillText('You: 350 points free · 600 staked', 28, h - 26)
}

const ORBS = [['Fix the quit hang', COLOR.tint, 'thinking'], ['Persona camera', COLOR.mint, 'idle'], ['Design tokens', COLOR.orange, 'needs you']]

export function orb(c, w, h) {
  // no glass: a volume has no bar; a dark translucent floor keeps the labels readable
  fillBox(c, 0, h * 0.62, w, h * 0.38, 20, 'rgba(20,26,44,0.55)')
  ORBS.forEach(([title, colour, status], i) => {
    const x = w * (0.2 + i * 0.3), y = h * 0.36, r = w * 0.09
    const g = c.createRadialGradient(x - r * 0.3, y - r * 0.3, r * 0.1, x, y, r)
    g.addColorStop(0, '#ffffff'); g.addColorStop(0.35, colour); g.addColorStop(1, 'rgba(0,0,0,0.35)')
    c.fillStyle = g; c.beginPath(); c.arc(x, y, r, 0, Math.PI * 2); c.fill()
    c.textAlign = 'center'; c.textBaseline = 'middle'
    c.font = `600 15px ${SANS}`; c.fillStyle = INK; c.fillText(title, x, h * 0.72)
    const chipW = capsuleWidth(c, status, 12) + 22; capsule(c, x - chipW / 2, h * 0.72 + 12, chipW, 22, status, { fill: status === "needs you" ? COLOR.orange : status === "thinking" ? COLOR.tint : CONTROL, colour: status === "idle" ? DIM : INK, size: 12, weight: 600 })
  })
  c.textAlign = 'left'; c.textBaseline = 'alphabetic'
  c.font = `500 13px ${SANS}`; c.fillStyle = DIM
  c.fillText('Look at an orb and pinch: its answer, or the window it belongs to', 16, h - 14)
}
